/**
 * Unilar Cart Recomendados
 * Infinite FancyApps Carousel with custom header navigation.
 *
 * Also handles add-to-cart loading UX: overlay while the item is added,
 * then skeleton + Product Recommendations re-fetch (cache bust) so the
 * added product is filtered out via Liquid is_in_cart.
 *
 * Script must be loaded from cart-drawer / cart page (not via morph HTML),
 * otherwise the custom element never upgrades after add-to-cart.
 */

if (!customElements.get('cart-recomendados-slider')) {
  class CartRecomendadosSlider extends HTMLElement {
    /** @type {import('fancyapps').Carousel | null} */
    #carousel = null;

    /** @type {ResizeObserver | null} */
    #resizeObserver = null;

    /** @type {number | null} */
    #rafId = null;

    /** @type {(() => void) | null} */
    #onPrev = null;

    /** @type {(() => void) | null} */
    #onNext = null;

    connectedCallback() {
      this.#init();
      this.#observeSize();
    }

    disconnectedCallback() {
      this.#teardownObservers();
      this.#destroyCarousel();
    }

    #teardownObservers() {
      if (this.#rafId != null) {
        cancelAnimationFrame(this.#rafId);
        this.#rafId = null;
      }

      this.#resizeObserver?.disconnect();
      this.#resizeObserver = null;
    }

    #unbindNav() {
      const root = this.closest('.cart-recomendados__section');
      const prevBtn = root?.querySelector('[data-cart-recomendados-prev]');
      const nextBtn = root?.querySelector('[data-cart-recomendados-next]');

      if (prevBtn && this.#onPrev) prevBtn.removeEventListener('click', this.#onPrev);
      if (nextBtn && this.#onNext) nextBtn.removeEventListener('click', this.#onNext);

      this.#onPrev = null;
      this.#onNext = null;
    }

    #destroyCarousel() {
      this.#unbindNav();

      if (this.#carousel) {
        this.#carousel.destroy();
        this.#carousel = null;
      }

      this.carousel = null;
      this.classList.remove('is-carousel-active');
    }

    #observeSize() {
      if (this.#resizeObserver) return;

      this.#resizeObserver = new ResizeObserver(() => {
        if (!this.isConnected) return;

        if (!this.#carousel) {
          this.#init();
          return;
        }

        if (this.offsetWidth > 0 && typeof this.#carousel.updateMetrics === 'function') {
          this.#carousel.updateMetrics();
        }
      });

      this.#resizeObserver.observe(this);
    }

    #init() {
      if (this.#rafId != null) {
        cancelAnimationFrame(this.#rafId);
        this.#rafId = null;
      }

      const start = () => {
        this.#rafId = null;

        if (!this.isConnected) return;

        if (!window.Carousel) {
          this.#rafId = requestAnimationFrame(start);
          return;
        }

        if (this.#carousel) return;

        const slides = this.querySelectorAll('.f-carousel__slide');
        if (slides.length === 0) return;

        // Wait until the drawer/summary has a real width.
        if (this.offsetWidth === 0) {
          this.#rafId = requestAnimationFrame(start);
          return;
        }

        this.classList.add('is-carousel-active');

        this.#carousel = new window.Carousel(this, {
          Dots: false,
          Navigation: false,
          infinite: true,
          center: false,
          fill: false,
          slidesPerPage: 1,
        });

        this.carousel = this.#carousel;
        this.#bindNav();

        requestAnimationFrame(() => {
          if (this.#carousel && typeof this.#carousel.updateMetrics === 'function') {
            this.#carousel.updateMetrics();
          }
        });
      };

      start();
    }

    #bindNav() {
      this.#unbindNav();

      const root = this.closest('.cart-recomendados__section');
      if (!root || !this.#carousel) return;

      const prevBtn = root.querySelector('[data-cart-recomendados-prev]');
      const nextBtn = root.querySelector('[data-cart-recomendados-next]');

      this.#onPrev = () => this.#carousel?.slidePrev();
      this.#onNext = () => this.#carousel?.slideNext();

      prevBtn?.addEventListener('click', this.#onPrev);
      nextBtn?.addEventListener('click', this.#onNext);
    }
  }

  customElements.define('cart-recomendados-slider', CartRecomendadosSlider);
}

/**
 * Add-to-cart loading + recommendations refresh for cart recommend cards.
 */
(() => {
  const LOADING_TIMEOUT_MS = 12000;

  /** @type {HTMLElement | null} */
  let pendingRoot = null;

  /** @type {number | undefined} */
  let loadingTimeoutId;

  /**
   * @param {Element | null | undefined} from
   * @returns {HTMLElement | null}
   */
  function getRecommendRoot(from) {
    const root = from?.closest?.('product-recommendations.cart-recomendados');
    return root instanceof HTMLElement ? root : null;
  }

  /**
   * @returns {HTMLElement | null}
   */
  function getLiveRecommendRoot() {
    const root = document.querySelector('product-recommendations.cart-recomendados');
    return root instanceof HTMLElement ? root : null;
  }

  /**
   * @param {HTMLElement | null} root
   */
  function setLoading(root) {
    if (!(root instanceof HTMLElement)) return;
    root.classList.add('is-loading');
    root.setAttribute('aria-busy', 'true');
  }

  /**
   * @param {HTMLElement | null} root
   */
  function clearLoading(root) {
    if (!(root instanceof HTMLElement)) return;
    root.classList.remove('is-loading');
    root.removeAttribute('aria-busy');
  }

  function clearPending() {
    if (loadingTimeoutId !== undefined) {
      clearTimeout(loadingTimeoutId);
      loadingTimeoutId = undefined;
    }
    pendingRoot = null;
  }

  /**
   * @param {HTMLElement} root
   */
  function armLoadingTimeout(root) {
    if (loadingTimeoutId !== undefined) clearTimeout(loadingTimeoutId);
    loadingTimeoutId = window.setTimeout(() => {
      clearLoading(getLiveRecommendRoot() || root);
      clearPending();
    }, LOADING_TIMEOUT_MS);
  }

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const root = getRecommendRoot(form);
      if (!root) return;

      pendingRoot = root;
      setLoading(root);
      armLoadingTimeout(root);
    },
    true
  );

  /**
   * @param {CustomEvent} event
   */
  function handleCartUpdate(event) {
    if (!pendingRoot) return;

    const root = pendingRoot;
    const didError = Boolean(event.detail?.data?.didError);

    // Wait for cart-items morph (+ preserve) to finish in this event turn.
    queueMicrotask(() => {
      requestAnimationFrame(() => {
        const liveRoot = getLiveRecommendRoot() || root;
        clearPending();

        if (didError) {
          clearLoading(liveRoot);
          return;
        }

        const reload = /** @type {{ reloadRecommendations?: (opts?: object) => Promise<void> }} */ (
          liveRoot
        ).reloadRecommendations;

        if (typeof reload !== 'function') {
          clearLoading(liveRoot);
          return;
        }

        setLoading(liveRoot);

        reload
          .call(liveRoot, { bustCache: true, showSkeleton: true })
          .catch((error) => {
            console.error('Cart recommendations refresh failed:', error);
          })
          .finally(() => {
            clearLoading(getLiveRecommendRoot() || liveRoot);
          });
      });
    });
  }

  document.addEventListener('cart:update', handleCartUpdate);

  document.addEventListener('cart:error', () => {
    if (!pendingRoot) return;
    clearLoading(getLiveRecommendRoot() || pendingRoot);
    clearPending();
  });
})();
